<template>
  <section class="dte-standard-box">
    <table class="dte-standard-table dte-standard-table--boxed dte-standard-table--detail">
      <thead>
        <tr class="dte-standard-table__title-row" :style="titleRowStyle">
          <td :colspan="colspan" class="dte-standard-table__title-cell">
            CUERPO DEL DOCUMENTO
          </td>
        </tr>
        <tr class="dte-standard-table__header-row">
          <th class="dte-standard-table__header-cell dte-standard-table__header-cell--small">{{ itemNumberLabel }}</th>
          <th v-if="showItemTypeColumn" class="dte-standard-table__header-cell dte-standard-table__header-cell--small">{{ itemTypeLabel }}</th>
          <th class="dte-standard-table__header-cell dte-standard-table__header-cell--small">{{ quantityLabel }}</th>
          <th v-if="showUnitColumn" class="dte-standard-table__header-cell dte-standard-table__header-cell--small">{{ unitLabel }}</th>
          <th v-if="showCodeColumn" class="dte-standard-table__header-cell dte-standard-table__header-cell--small">{{ codeLabel }}</th>
          <th v-if="showAlternateCodeColumn" class="dte-standard-table__header-cell dte-standard-table__header-cell--small">{{ alternateCodeLabel }}</th>
          <th class="dte-standard-table__header-cell">{{ descriptionLabel }}</th>
          <th class="dte-standard-table__header-cell dte-standard-table__header-cell--amount">{{ unitPriceLabel }} {{ documentData.meta.currency }}</th>
          <th v-if="showDiscountColumn" class="dte-standard-table__header-cell dte-standard-table__header-cell--amount">{{ discountLabel }} {{ documentData.meta.currency }}</th>
          <th v-if="showExemptColumn" class="dte-standard-table__header-cell dte-standard-table__header-cell--amount">{{ exemptLabel }} {{ documentData.meta.currency }}</th>
          <th v-if="showNonSubjectColumn" class="dte-standard-table__header-cell dte-standard-table__header-cell--amount">{{ nonSubjectLabel }} {{ documentData.meta.currency }}</th>
          <th v-if="showNonTaxedColumn" class="dte-standard-table__header-cell dte-standard-table__header-cell--amount">{{ nonTaxableLabel }} {{ documentData.meta.currency }}</th>
          <th class="dte-standard-table__header-cell dte-standard-table__header-cell--amount">{{ amountLabel }} {{ documentData.meta.currency }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in page.items" :key="`standard-item-${page.number}-${item.fragmentKey || item.index}`" class="dte-standard-table__detail-row">
          <td class="dte-standard-table__right">{{ item._showLeadingValues === false ? "" : item.index }}</td>
          <td v-if="showItemTypeColumn" class="dte-standard-table__center">{{ item._showLeadingValues === false ? "" : (item.itemType || "3") }}</td>
          <td class="dte-standard-table__right">{{ item._showLeadingValues === false ? "" : item.quantity }}</td>
          <td v-if="showUnitColumn">{{ item._showLeadingValues === false ? "" : (item.unit || "") }}</td>
          <td v-if="showCodeColumn">{{ item._showLeadingValues === false ? "" : (item.code || "") }}</td>
          <td v-if="showAlternateCodeColumn">{{ item._showLeadingValues === false ? "" : (item.barcode || "") }}</td>
          <td class="dte-standard-table__multiline">{{ item.description }}</td>
          <td class="dte-standard-table__right">{{ item._showLeadingValues === false ? "" : formatPlain(unitPrice(item)) }}</td>
          <td v-if="showDiscountColumn" class="dte-standard-table__right">{{ item._showLeadingValues === false ? "" : formatPlain(item.discount) }}</td>
          <td v-if="showExemptColumn" class="dte-standard-table__right">{{ item._showLeadingValues === false ? "" : formatPlain(item.exempt) }}</td>
          <td v-if="showNonSubjectColumn" class="dte-standard-table__right">{{ item._showLeadingValues === false ? "" : formatPlain(item.nonSubject) }}</td>
          <td v-if="showNonTaxedColumn" class="dte-standard-table__right">{{ item._showLeadingValues === false ? "" : formatPlain(item.nonTaxed) }}</td>
          <td class="dte-standard-table__right">{{ item._showLeadingValues === false ? "" : formatPlain(mainAmount(item)) }}</td>
        </tr>
        <tr v-if="!page.items.length && !page.showTail">
          <td :colspan="colspan" class="dte-standard-table__empty">
            Documento sin lineas de detalle
          </td>
        </tr>
        <tr v-if="page.showTail" class="dte-standard-table__tail-row">
          <td :colspan="tailLeftColspan" class="dte-standard-table__tail-cell dte-standard-table__tail-cell--words">
            <b>Valor en letras:</b>
            <p>
              {{ documentData.amountInWords || "---" }}
            </p>
              
          </td>
          <td :colspan="tailRightColspan" class="dte-standard-table__tail-cell dte-standard-table__tail-cell--totals">
            <table class="dte-standard-totals-table">
              <tbody>
                <tr
                  v-for="(row, index) in totalRows"
                  :key="`${row.key || row.label}-${index}`"
                  :class="{ 'is-emphasis': row.emphasis }"
                >
                  <td>{{ row.displayLabel || row.label }}</td>
                  <td class="dte-standard-totals-table__amount">
                    <template v-if="row.currency">
                      <span class="dte-standard-totals-table__currency-inline">{{ row.currency }}</span>
                      <span class="dte-standard-totals-table__value-inline">{{ row.value }}</span>
                    </template>
                    <template v-else>
                      <span class="dte-standard-totals-table__value-inline">{{ row.value }}</span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        <tr v-if="page.showTail && documentData.notes">
          <td :colspan="colspan" class="dte-standard-table__tail-cell dte-standard-table__tail-cell--notes">
            <b>Observaciones:</b><br> {{ documentData.notes }}
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script>
import { formatPlainMoney } from "../utils/dtePrintFormatters";
import {
  getStandardThemeColors,
  getStandardTotalRows,
} from "../utils/dtePrintStandard";
import { getDteTemplateLabel, isDteTemplateFieldVisible } from "@/helpers/dteTemplateSettings";

export default {
  name: "DteStandardDetailSection",
  props: {
    documentData: {
      type: Object,
      required: true,
    },
    page: {
      type: Object,
      required: true,
    },
  },
  computed: {
    templateSettings() {
      return this.documentData.templateSettings || {};
    },
    showItemTypeColumn() {
      return isDteTemplateFieldVisible(this.templateSettings, "detail", "itemType");
    },
    titleRowStyle() {
      const colors = getStandardThemeColors(this.documentData);
      return {
        backgroundColor: colors.bg,
        color: colors.fg,
      };
    },
    showUnitColumn() {
      return (
        isDteTemplateFieldVisible(this.templateSettings, "detail", "unit") &&
        this.documentData.items.some((item) => item.unit)
      );
    },
    showCodeColumn() {
      return (
        isDteTemplateFieldVisible(this.templateSettings, "detail", "code") &&
        this.documentData.items.some((item) => item.code)
      );
    },
    showAlternateCodeColumn() {
      return (
        isDteTemplateFieldVisible(this.templateSettings, "detail", "alternateCode") &&
        this.documentData.items.some((item) => item.barcode)
      );
    },
    showDiscountColumn() {
      return (
        this.documentData.meta.invoiceType !== "CDD" &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "discount")
      );
    },
    showExemptColumn() {
      return (
        !["FEX", "CDD"].includes(this.documentData.meta.invoiceType) &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "exemptSales")
      );
    },
    showNonSubjectColumn() {
      return (
        !["FEX", "CDD"].includes(this.documentData.meta.invoiceType) &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "nonSubjectSales")
      );
    },
    showNonTaxedColumn() {
      return (
        ["FCF", "CCF", "FEX"].includes(this.documentData.meta.invoiceType) &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "nonTaxableSales")
      );
    },
    codeLabel() {
      return getDteTemplateLabel(this.templateSettings, "code", "Codigo");
    },
    itemNumberLabel() {
      return getDteTemplateLabel(this.templateSettings, "itemNumber", "No. Item");
    },
    itemTypeLabel() {
      return getDteTemplateLabel(this.templateSettings, "itemType", "Tipo item");
    },
    quantityLabel() {
      return getDteTemplateLabel(this.templateSettings, "quantity", "Cant.");
    },
    descriptionLabel() {
      return getDteTemplateLabel(this.templateSettings, "description", "Descripcion");
    },
    alternateCodeLabel() {
      return getDteTemplateLabel(this.templateSettings, "alternateCode", "Codigo alterno");
    },
    unitLabel() {
      return getDteTemplateLabel(this.templateSettings, "unit", "Unidad");
    },
    unitPriceLabel() {
      return getDteTemplateLabel(this.templateSettings, "unitPrice", "Precio unitario");
    },
    discountLabel() {
      return getDteTemplateLabel(this.templateSettings, "discount", "Descuento");
    },
    exemptLabel() {
      return getDteTemplateLabel(this.templateSettings, "exemptSales", "Ventas exentas");
    },
    nonSubjectLabel() {
      return getDteTemplateLabel(this.templateSettings, "nonSubjectSales", "Ventas no sujetas");
    },
    nonTaxableLabel() {
      return getDteTemplateLabel(this.templateSettings, "nonTaxableSales", "Otros montos no afectos");
    },
    amountLabel() {
      if (this.documentData.meta.invoiceType === "FEX") {
        return getDteTemplateLabel(this.templateSettings, "mainAmount", "Ventas afectas");
      }

      if (this.documentData.meta.invoiceType === "CDD") {
        return getDteTemplateLabel(this.templateSettings, "mainAmount", "Valor donado");
      }

      return getDteTemplateLabel(this.templateSettings, "mainAmount", "Ventas gravadas");
    },
    colspan() {
      return 5 +
        (this.showItemTypeColumn ? 1 : 0) +
        (this.showUnitColumn ? 1 : 0) +
        (this.showCodeColumn ? 1 : 0) +
        (this.showAlternateCodeColumn ? 1 : 0) +
        (this.showDiscountColumn ? 1 : 0) +
        (this.showExemptColumn ? 1 : 0) +
        (this.showNonSubjectColumn ? 1 : 0) +
        (this.showNonTaxedColumn ? 1 : 0);
    },
    tailRightColspan() {
      return this.colspan >= 6 ? 3 : 2;
    },
    tailLeftColspan() {
      return this.colspan - this.tailRightColspan;
    },
    totalRows() {
      const resolvedRows =
        Array.isArray(this.documentData.totalRows) && this.documentData.totalRows.length
          ? [...this.documentData.totalRows]
          : getStandardTotalRows(this.documentData);
      const header = this.documentData?.rawData?.header || {};
      const totalDiscount = Number(header.InvoiceTotalDescu || 0);
      const hasDiscountRow = resolvedRows.some((row) => row?.key === "discounts");

      if (totalDiscount > 0 && !hasDiscountRow) {
        const discountRow = {
          key: "discounts",
          label: "Total descuentos",
          displayLabel: getDteTemplateLabel(
            this.templateSettings,
            "discounts",
            "Total descuentos"
          ),
          currency: header.InvoiceCurrency || "$",
          value: this.formatPlain(totalDiscount),
          emphasis: false,
        };
        const totalIndex = resolvedRows.findIndex((row) => row?.key === "total");

        if (totalIndex >= 0) {
          resolvedRows.splice(totalIndex, 0, discountRow);
        } else {
          resolvedRows.push(discountRow);
        }
      }

      return resolvedRows;
    },
  },
  methods: {
    formatPlain(value) {
      return formatPlainMoney(value, 2);
    },
    mainAmount(item) {
      if (this.documentData.meta.invoiceType === "CDD") {
        return item.total;
      }

      if (this.documentData.meta.invoiceType === "FEX") {
        return item.taxed || item.total;
      }

      return item.taxed;
    },
    unitPrice(item) {
      return item.nonTaxed > 0 ? 0 : item.unitPrice;
    },
  },
};
</script>
